import { corsproxyRequest } from "./corsproxyRequest";

interface GeoapifyFeature {
  type: "Feature";
  properties: {
    name: string;
    admin_level?: string;
    type: string;
    display_name: string;
    postcode?: string;
    city?: string;
    country?: string;
    state?: string;
  };
  geometry: {
    type: "Polygon" | "MultiPolygon";
    coordinates: number[][][] | number[][][][];
  };
}

interface GeoapifyProperties {
  name?: string;
  city?: string;
  county?: string;
  state?: string;
  country?: string;
  postcode?: string;
  formatted?: string;
  result_type?: string;
  rank?: {
    importance?: number;
  };
}

interface GeoapifyGeocodingResponse {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    properties: GeoapifyProperties & {
      place_id?: string;
    };
    geometry: {
      type: string;
      coordinates: any;
    };
    bbox?: number[];
  }>;
}

interface GeoapifyBoundariesResponse {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    properties: any;
    geometry: {
      type: string;
      coordinates: number[][][] | number[][][][];
    };
  }>;
}

/**
 * Search for locations using Geoapify Geocoding API
 * Returns GeoJSON features with polygon/multipolygon geometries
 */
export const searchGeoapify = async (
  searchQuery: string,
  options: {
    countryCode?: string;
    stateCode?: string;
    filterTypes?: string[];
  } = {},
): Promise<GeoapifyFeature[]> => {
  const {
    countryCode,
    stateCode,
    filterTypes = ["administrative", "city", "suburb", "postcode"],
  } = options;

  try {
    // Construct the query with state and country
    let fullQuery = searchQuery;
    if (stateCode) {
      fullQuery = `${fullQuery}, ${stateCode}`;
    }
    if (countryCode) {
      fullQuery = `${fullQuery}, ${countryCode}`;
    }

    // Geoapify API key
    const apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY;
    if (!apiKey) {
      throw new Error("VITE_GEOAPIFY_API_KEY is not configured");
    }

    // Step 1: Geocode to get the place_id
    // Use the filterTypes to determine what type to search for
    const typeParam = filterTypes.includes("postcode")
      ? "postcode"
      : filterTypes.includes("city")
        ? "city"
        : "amenity";

    const geocodeUrl = `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(
      fullQuery,
    )}&type=${typeParam}&format=geojson&apiKey=${apiKey}`;

    console.log(
      "Geoapify geocoding request:",
      geocodeUrl.replace(apiKey, "REDACTED"),
    );

    const geocodeResponse: GeoapifyGeocodingResponse =
      await corsproxyRequest(geocodeUrl);

    console.log("Geoapify geocoding response:", geocodeResponse);
    console.log(
      "First result type:",
      geocodeResponse.features?.[0]?.properties?.result_type,
    );

    if (!geocodeResponse || geocodeResponse.type !== "FeatureCollection") {
      console.error("Invalid geocoding response format:", geocodeResponse);
      throw new Error("Invalid response format from Geoapify");
    }

    if (!geocodeResponse.features || geocodeResponse.features.length === 0) {
      console.log("No results from Geoapify geocoding");
      return [];
    }

    // Get the first result's place_id
    const firstResult = geocodeResponse.features[0];
    const placeId = firstResult.properties.place_id;

    if (!placeId) {
      console.log("No place_id in Geoapify result");
      return [];
    }

    console.log("Found place_id:", placeId);

    // Step 2: Fetch the boundary geometry for this specific place
    // Try place-details endpoint which should return the boundary of the place itself
    const boundariesUrl = `https://api.geoapify.com/v2/place-details?id=${placeId}&features=details&apiKey=${apiKey}`;

    console.log(
      "Geoapify place-details request:",
      boundariesUrl.replace(apiKey, "REDACTED"),
    );

    const boundariesResponse: any = await corsproxyRequest(boundariesUrl);

    console.log("Geoapify place-details response:", boundariesResponse);

    // Check if the response has a feature with geometry
    if (!boundariesResponse || !boundariesResponse.features) {
      console.log(
        "No features in place-details response, trying different approach",
      );

      // Fallback: Try to get boundary using admin boundaries endpoint
      // This might not work for all places but worth trying
      return [];
    }

    // The place-details response should contain the feature with its geometry
    const placeFeature = boundariesResponse.features?.[0];

    if (!placeFeature || !placeFeature.geometry) {
      console.log("No geometry in place-details response");
      return [];
    }

    // Check if it's a polygon geometry
    if (
      placeFeature.geometry.type !== "Polygon" &&
      placeFeature.geometry.type !== "MultiPolygon"
    ) {
      console.log(
        "Place-details returned non-polygon geometry:",
        placeFeature.geometry.type,
      );
      return [];
    }

    // Determine admin level from result type
    const props = firstResult.properties;
    let adminLevel = "unknown";
    if (props.result_type) {
      if (props.result_type.includes("postcode")) adminLevel = "postcode";
      else if (props.result_type.includes("city")) adminLevel = "8";
      else if (props.result_type.includes("county")) adminLevel = "6";
      else if (props.result_type.includes("state")) adminLevel = "4";
      else if (props.result_type.includes("country")) adminLevel = "2";
    }

    // Convert to our feature format
    const features = [
      {
        type: "Feature" as const,
        properties: {
          name:
            props.name ||
            props.city ||
            props.formatted?.split(",")[0] ||
            searchQuery,
          admin_level: adminLevel,
          type: props.result_type || "unknown",
          display_name:
            props.formatted ||
            `${props.name || ""}, ${props.state || ""}, ${props.country || ""}`,
          postcode: props.postcode,
          city: props.city,
          country: props.country,
          state: props.state,
        },
        geometry: {
          type: placeFeature.geometry.type as "Polygon" | "MultiPolygon",
          coordinates: placeFeature.geometry.coordinates,
        },
      },
    ];

    console.log(
      `Found ${features.length} polygon features from Geoapify boundaries`,
    );
    return features;
  } catch (error) {
    console.error("Error in searchGeoapify:", error);
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      throw new Error(
        "Network error: Unable to connect to Geoapify. Please check your internet connection.",
      );
    }
    throw error;
  }
};
